import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from 'sonner';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import './App.css';

// Pages
import LandingPage from './pages/LandingPage';
import ContactPage from './pages/ContactPage';
import PolicyPage from './pages/PolicyPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import MagicLoginPage from './pages/MagicLoginPage';
import AuthCallback from './pages/AuthCallback';

// Admin Pages
import AdminDashboard from './pages/admin/Dashboard';
import AdminCases from './pages/admin/Cases';
import AdminCaseDetail from './pages/admin/CaseDetail';
import AdminCompanies from './pages/admin/Companies';
import AdminCompanyDetail from './pages/admin/CompanyDetail';
import AdminProxies from './pages/admin/Proxies';
import AdminContracts from './pages/admin/Contracts';
import AdminEmailTemplates from './pages/admin/EmailTemplates';
import AdminUsers from './pages/admin/Users';
import AdminNotifications from './pages/admin/Notifications';
import AdminQuotations from './pages/admin/Quotations';
import AdminInvoices from './pages/admin/Invoices';
import AdminInvoiceSettings from './pages/admin/InvoiceSettings';
import AdminReports from './pages/admin/Reports';

// Client Portal Pages
import PortalDashboard from './pages/portal/Dashboard';
import PortalCaseDetail from './pages/portal/CaseDetail';
import PortalServiceRequest from './pages/portal/ServiceRequest';
import PortalQuotations from './pages/portal/Quotations';
import PortalStatement from './pages/portal/Statement';
import PortalQuotationDetail from './pages/portal/QuotationDetail';
import PortalProfile from './pages/portal/Profile';
import PortalHelp from './pages/portal/Help';
import PortalSubAccounts from './pages/portal/SubAccounts';
import PortalRegisterPage from './pages/portal/RegisterPage';
import ClientLoginPage from './pages/portal/LoginPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = '/api';

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Axios instance
export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Auto-refresh interceptor: if a request returns 401, try /auth/refresh once
// and retry the original request. Prevents users from being kicked out mid-task
// when their short-lived access token expires.
let _refreshPromise = null;
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    const status = error.response?.status;
    const url = originalRequest.url || '';
    const isAuthEndpoint =
      url.includes('/auth/login') ||
      url.includes('/auth/refresh') ||
      url.includes('/auth/me') ||
      url.includes('/auth/register') ||
      url.includes('/auth/logout');

    if (status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;
      try {
        if (!_refreshPromise) {
          _refreshPromise = api.post('/auth/refresh').finally(() => {
            _refreshPromise = null;
          });
        }
        await _refreshPromise;
        return api(originalRequest);
      } catch (refreshError) {
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

// Error formatter
export const formatApiError = (error) => {
  const detail = error.response?.data?.detail;
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

// Auth Provider
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    setUser(data);
    return data;
  };

  const register = async (email, password, name, role = 'client') => {
    const { data } = await api.post('/auth/register', { email, password, name, role });
    setUser(data);
    return data;
  };

  const logout = async () => {
    await api.post('/auth/logout');
    setUser(null);
  };

  const setAuthUser = (userData) => {
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth, setAuthUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#0055FF] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#4B5563]">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    // Redirect to appropriate login based on attempted route
    const isPortalRoute = location.pathname.startsWith('/portal');
    return <Navigate to={isPortalRoute ? "/portal/login" : "/login"} state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    if (user.role === 'client') {
      return <Navigate to="/portal" replace />;
    }
    return <Navigate to="/admin" replace />;
  }

  return children;
};

// App Router
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id (OAuth callback) synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/portal/login" element={<ClientLoginPage />} />
      <Route path="/portal/register" element={<PortalRegisterPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/magic-login" element={<MagicLoginPage />} />

      {/* Policy Pages */}
      <Route path="/terms-and-conditions" element={<PolicyPage />} />
      <Route path="/privacy-policy" element={<PolicyPage />} />
      <Route path="/paia-manual" element={<PolicyPage />} />
      <Route path="/disclaimer" element={<PolicyPage />} />
      <Route path="/cookies-policy" element={<PolicyPage />} />

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/cases"
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminCases />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/cases/:caseId"
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminCaseDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/companies"
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminCompanies />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/companies/:companyId"
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminCompanyDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/proxies"
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminProxies />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/contracts"
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminContracts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/quotations"
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminQuotations />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/invoices"
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminInvoices />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/invoicing-settings"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminInvoiceSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/email-templates"
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminEmailTemplates />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/notifications"
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminNotifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/reports"
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminReports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminUsers />
          </ProtectedRoute>
        }
      />

      {/* Client Portal Routes */}
      <Route
        path="/portal"
        element={
          <ProtectedRoute>
            <PortalDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal/cases/:caseId"
        element={
          <ProtectedRoute>
            <PortalCaseDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal/quotations"
        element={
          <ProtectedRoute>
            <PortalQuotations />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal/statement"
        element={
          <ProtectedRoute>
            <PortalStatement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal/quotations/:quotationId"
        element={
          <ProtectedRoute>
            <PortalQuotationDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal/service-request"
        element={
          <ProtectedRoute>
            <PortalServiceRequest />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal/profile"
        element={
          <ProtectedRoute>
            <PortalProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal/help"
        element={
          <ProtectedRoute>
            <PortalHelp />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal/sub-accounts"
        element={
          <ProtectedRoute>
            <PortalSubAccounts />
          </ProtectedRoute>
        }
      />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  const recaptchaKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  return (
    <GoogleReCaptchaProvider reCaptchaKey={recaptchaKey || ''}>
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </GoogleReCaptchaProvider>
  );
}

export default App;
